import { AutoScouter } from '../../app/feathers/auto-scouter/AutoScouter';
import type { Step, StepStatus } from '../../components/StepIndicator';

export type AutoScouterListState =
  | 'crawling-needs-review'
  | 'parsing-needs-review'
  | 'matching-needs-review'
  | 'ready-to-create'
  | 'completed';

export const autoScouterStateOptions: Array<{ label: string; value: AutoScouterListState }> = [
  { label: 'Crawling', value: 'crawling-needs-review' },
  { label: 'Parsing', value: 'parsing-needs-review' },
  { label: 'Matching', value: 'matching-needs-review' },
  { label: 'Ready to create', value: 'ready-to-create' },
  { label: 'Completed', value: 'completed' },
];

export const getAutoScouterStateQuery = (
  state: AutoScouterListState | null
): Record<string, any> => {
  switch (state) {
    case 'crawling-needs-review':
      return {
        crawlStatus: { $ne: 'in-progress' },
        'review.url': { $ne: true },
      };
    case 'parsing-needs-review':
      return {
        'review.url': true,
        parseStatus: { $ne: 'in-progress' },
        'review.content': { $ne: true },
      };
    case 'matching-needs-review':
      return {
        'review.content': true,
        matchStatus: { $ne: 'in-progress' },
        'review.matching': { $ne: true },
      };
    case 'ready-to-create':
      return {
        'review.matching': true,
        'processInProgress.stage': { $ne: 'creating' },
        $or: [
          { createStatus: { $nin: ['in-progress', 'success'] } },
          { 'dataOperations.status': 'failed' },
        ],
      };
    case 'completed':
      return {
        createStatus: 'success',
        'dataOperations.status': { $ne: 'failed' },
      };
    default:
      return {};
  }
};

type ReviewStep = {
  name: string;
  isInProgress: boolean;
  isComplete: boolean;
  hasWarning?: boolean;
};

export const hasCreateError = (autoScouter: AutoScouter): boolean => {
  const operations = autoScouter.dataOperations || [];
  if (operations.some(operation => operation.status === 'failed')) {
    return true;
  }
  if (
    autoScouter.createStatus === 'success' ||
    operations.some(operation => operation.status === 'corrected')
  ) {
    return false;
  }
  return autoScouter.createStatus === 'error' || Boolean(autoScouter.dataOperationsError);
};

export const getAutoScouterSteps = (autoScouter: AutoScouter): Step[] => {
  const steps: ReviewStep[] = [
    {
      name: 'Crawling',
      isInProgress: autoScouter.crawlStatus === 'in-progress',
      isComplete: autoScouter.review?.url === true,
    },
    {
      name: 'Parsing',
      isInProgress: autoScouter.parseStatus === 'in-progress',
      isComplete: autoScouter.review?.content === true,
    },
    {
      name: 'Matching',
      isInProgress: autoScouter.matchStatus === 'in-progress',
      isComplete: autoScouter.review?.matching === true,
    },
    {
      name: 'Creating',
      isInProgress:
        autoScouter.createStatus === 'in-progress' ||
        autoScouter.processInProgress?.stage === 'creating',
      isComplete: autoScouter.createStatus === 'success',
      hasWarning: hasCreateError(autoScouter),
    },
  ];

  return steps.map((step, index) => {
    let status: StepStatus = 'pending';

    if (step.isInProgress) {
      status = 'in-progress';
    } else if (step.hasWarning) {
      status = 'warning';
    } else if (step.isComplete) {
      status = 'completed';
    } else if (index === 0 || steps[index - 1].isComplete) {
      status = 'active';
    }

    return { name: step.name, status };
  });
};
